import type { ComponentType, ReactNode, SVGProps } from 'react';
import { useLocation } from 'react-router';
import { Button } from '../../components/button';
import {
  AlertIcon,
  GaugeIcon,
  ListIcon,
  MailIcon,
  MailPlusIcon,
  PlugIcon,
} from '../../components/icons';
import { ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { formatDateTime } from '../services/format';
import { useMailboxes } from './api';
import { MailboxList } from './mailbox-list';

/**
 * Mailboxes (/app/mailboxes, `mail.mailboxes.view`) — Bekebi's email administration, inspired by
 * the Mail Server's own user list but governed by the portal's rules. What the page shows comes
 * only from GET /api/mail/mailboxes: while the Mail Server is not connected (or cannot be reached)
 * it says so and shows no mailbox, figure or control; once the API answers, it lists the mailboxes
 * with exactly the facts the Mail Server reported. Adding a mailbox and changing its size are
 * separate pages; the API enforces the size rules on every request.
 */
export function MailboxesPage() {
  const { state, refresh, isFetching } = useMailboxes({ poll: true });
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;
  const retry = () => void refresh();

  return (
    <div className="max-w-6xl space-y-6">
      <div>
        <h1>Mailboxes</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted">
          Manage Bekebi&rsquo;s email mailboxes and their storage limits from one place.
        </p>
      </div>

      {notice ? (
        <p role="status" className="rounded-md bg-success-soft p-3 text-sm text-success">
          {notice}
        </p>
      ) : null}

      {state.kind === 'loading' ? (
        <div className="rounded-[var(--radius-card)] border border-line bg-surface">
          <LoadingState label="Checking the Mail Server connection…" />
        </div>
      ) : state.kind === 'error' ? (
        <ErrorState
          title="Mailboxes couldn’t be loaded"
          message={`${state.message} Nothing has been changed.`}
          onRetry={retry}
        />
      ) : state.kind === 'connected' ? (
        <>
          <SyncStatus
            updatedAt={state.updatedAt}
            refreshFailed={state.refreshFailed}
            refreshing={isFetching}
            onRefresh={retry}
          />
          <MailboxList mailboxes={state.mailboxes} />
        </>
      ) : (
        <ConnectionPanel
          {...PANELS[state.kind]}
          action={
            state.kind === 'unreachable' ? (
              <Button variant="secondary" onClick={retry} disabled={isFetching}>
                {isFetching ? 'Checking…' : 'Check again'}
              </Button>
            ) : null
          }
        >
          <Capabilities />
        </ConnectionPanel>
      )}
    </div>
  );
}

/**
 * When the list was last confirmed by the Mail Server, and a manual refresh. The list also refreshes
 * by itself every minute while the page is visible; it is never presented as instantaneous.
 */
function SyncStatus({
  updatedAt,
  refreshFailed,
  refreshing,
  onRefresh,
}: {
  updatedAt: number;
  refreshFailed: boolean;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const when = formatDateTime(new Date(updatedAt).toISOString());
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <p className="text-muted">
          From the Mail Server at <span className="text-fg-2">{when}</span>. Refreshes every minute
          while this page is open.
        </p>
        <Button variant="secondary" onClick={onRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>
      {refreshFailed ? (
        <p
          role="status"
          className="rounded-lg border border-warning-line bg-warning-soft px-4 py-3 text-sm text-fg"
        >
          The latest refresh from the Mail Server failed, so this list may be out of date — it is
          the list confirmed at {when}. Nothing has been changed. The portal will try again.
        </p>
      ) : null}
    </div>
  );
}

type Tone = 'neutral' | 'warning';
type IconType = ComponentType<SVGProps<SVGSVGElement>>;

const PANELS: Record<
  'not_connected' | 'unreachable',
  { tone: Tone; badge: IconType; chip: string; title: string; description: string }
> = {
  not_connected: {
    tone: 'neutral',
    badge: PlugIcon,
    chip: 'Not connected',
    title: 'Mailboxes aren’t connected yet',
    description:
      'Mailbox management isn’t connected to the Mail Server yet, so no mailboxes can be shown or changed here. Once the connection is set up, mailboxes will appear on this page.',
  },
  unreachable: {
    tone: 'warning',
    badge: AlertIcon,
    chip: 'Unreachable',
    title: 'The Mail Server can’t be reached right now',
    description:
      'The portal couldn’t get mailbox information from the Mail Server just now, so none is shown. Nothing has been changed.',
  },
};

const TONES: Record<Tone, { chip: string; badge: string }> = {
  neutral: { chip: 'border-line-strong text-muted', badge: 'bg-raised text-muted' },
  warning: { chip: 'border-warning-line text-warning', badge: 'bg-warning-soft text-warning' },
};

/** The Mail Server connection as the API reports it, with what the page will offer below. */
function ConnectionPanel({
  tone,
  badge: Badge,
  chip,
  title,
  description,
  action,
  children,
}: (typeof PANELS)[keyof typeof PANELS] & { action: ReactNode; children: ReactNode }) {
  return (
    <section
      aria-labelledby="mailboxes-connection"
      className="rounded-[var(--radius-card)] border border-line bg-surface"
    >
      <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-start sm:p-8">
        <span className="relative flex size-14 shrink-0 items-center justify-center rounded-2xl bg-info-soft text-accent-link">
          <MailIcon className="size-7" />
          <span
            className={`absolute -bottom-1.5 -right-1.5 flex size-7 items-center justify-center rounded-full border-[3px] border-surface ${TONES[tone].badge}`}
          >
            <Badge className="size-3.5" />
          </span>
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h2 id="mailboxes-connection" className="text-lg">
              {title}
            </h2>
            <span
              className={`whitespace-nowrap rounded-full border px-2 py-px text-[0.62rem] font-semibold uppercase tracking-wide ${TONES[tone].chip}`}
            >
              {chip}
            </span>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-fg-2">{description}</p>
          {action ? <div className="mt-4">{action}</div> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

/**
 * What this page offers once the Mail Server answers — explanatory copy, never controls. Only the
 * operations approved for the signed-in user's permissions are described.
 */
function Capabilities() {
  const canManage = useHasPermission('mail.mailboxes.manage');
  const unlimited = useHasPermission('mail.mailboxes.manage_unlimited');

  const items: { icon: IconType; title: string; text: string }[] = [
    {
      icon: ListIcon,
      title: 'See every mailbox',
      text: 'Each mailbox address with its size limit and, where the Mail Server reports it, the space it uses.',
    },
  ];
  if (canManage) {
    items.push(
      {
        icon: MailPlusIcon,
        title: 'Add a mailbox',
        text: 'Create a new Bekebi email address with its own size limit, from 1 GB.',
      },
      {
        icon: GaugeIcon,
        title: 'Change a mailbox’s size',
        text: unlimited
          ? 'Set how much space a mailbox can use, from 1 GB, including above 5 GB.'
          : 'Set how much space a mailbox can use, from 1 GB up to 5 GB. Larger mailboxes are handled by the Admin.',
      },
    );
  }

  return (
    <div className="border-t border-line p-5 sm:p-8">
      <h3
        id="mailboxes-capabilities"
        className="text-xs font-semibold uppercase tracking-wide text-muted"
      >
        What you&rsquo;ll be able to do here
      </h3>
      <ul aria-labelledby="mailboxes-capabilities" className="mt-4 grid gap-3 sm:grid-cols-3">
        {items.map(({ icon: ItemIcon, title, text }) => (
          <li
            key={title}
            className="flex gap-3 rounded-xl border border-line bg-canvas/40 p-4 sm:flex-col"
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-raised text-fg-2">
              <ItemIcon className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-fg">{title}</p>
              <p className="mt-1 text-sm text-muted">{text}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
